import { c as _c } from "react/compiler-runtime";
import type { Route } from 'next';
import Link from 'next/link';
import { AGENTS_PATH, STANDARDS_PATH } from '@/constants/agent-catalog';
import { AI_CODE_REVIEW_PATH } from '@/constants/ai-code-review';
import { BUG_BENCH_NAME, BUG_BENCH_PATH } from '@/constants/bug-bench';
import { bugBenchPaths } from '@/lib/bug-bench/urls';
import { cn } from '@/lib/utils';
const RESOURCES = [{
  label: 'AI code review',
  href: AI_CODE_REVIEW_PATH
}, {
  label: BUG_BENCH_NAME,
  href: BUG_BENCH_PATH
},
// Bug Bench's numbers as citable answers. Every public page links it, so search engines
// and readers find it from outside Bug Bench too; it is noindex only while no model is ranked.
{
  label: 'AI coding statistics',
  href: bugBenchPaths.stats
}, {
  label: 'Coding standards',
  href: STANDARDS_PATH
}, {
  label: 'Agents',
  href: AGENTS_PATH
}, {
  label: 'Wikis',
  href: '/wikis'
}] as const satisfies readonly {
  label: string;
  href: Route;
}[];

/** The hub a public page belongs to, by the hub's path. */
export type PublicResourceHref = (typeof RESOURCES)[number]['href'];
type PublicResourcesNavProps = {
  current: PublicResourceHref | null;
  /** Spacing overrides for a row placed inside a padded column instead of at a page's edge. */
  className?: string;
};

/**
 * Resources row that ends the public pages (Bug Bench, the agent catalog, wikis), so readers and
 * crawlers can move between cubic's resource hubs. Layouts render it after the page, inside
 * BaseTemplate's scrolling main, so it scrolls with the content instead of covering it. The wiki
 * pages place it themselves, because the wiki viewer scrolls inside its own columns.
 */
export function PublicResourcesNav(t0) {
  const $ = _c(10);
  const {
    current,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("border-border border-t px-7 pt-8 pb-16 md:px-12", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-muted-foreground text-xs">Resources</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== current) {
    t3 = RESOURCES.map(t4 => {
      const {
        label,
        href
      } = t4;
      return <li key={href}><Link href={href} aria-current={href === current ? "page" : undefined} className={cn("hover:text-foreground transition-colors", href === current ? "text-foreground font-medium" : "text-muted-foreground")}>{label}</Link></li>;
    });
    $[3] = current;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:gap-8">{t2}<ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm">{t3}</ul></div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <nav aria-label="Resources" className={t1}>{t4}</nav>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}